import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Paper, TableBody, TableCell, TableRow } from "@mui/material";
import styled from "@emotion/styled";

import Pagination from "Components/Common/BaseTable/Pagination";
import { usePagination } from "Components/Common/BaseTable";
import { StyledTable, StyledTableCell, StyledTableHead } from "./style";

const allClassifications = [
  "A(i)",
  "A(ii)",
  "A(iii)",
  "B(i)",
  "B(ii)",
  "C(i)",
  "C(ii)",
  "D(i)",
  "D(ii)",
  "E(i)",
  "E(ii)",
  "F(i)",
  "F(ii)",
  "F(iii)",
  "F(iv)",
  "G",
  "H",
];

const RECORDS_PER_PAGE = 10;

const StyledTableContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "maxHeight",
})`
  max-height: ${({ maxHeight }) => maxHeight ?? "unset"};
  overflow-y: auto;
  .MuiPaper-root {
    overflow-x: scroll;
  }
`;

const ComplaintTable = ({ data, maxHeight, t }) => {
  const paginate = usePagination();
  const [rowData, setRowData] = useState([]);
  const { page, setTotalPages } = paginate;

  useEffect(() => {
    const totalRows = data?.[0]?.consumerComplaintDataList || [];

    setTotalPages(Math.ceil(totalRows.length / RECORDS_PER_PAGE));

    const rowData = totalRows.slice(
      RECORDS_PER_PAGE * (page - 1),
      RECORDS_PER_PAGE * page
    );

    setRowData(rowData);
  }, [page, RECORDS_PER_PAGE, data, setTotalPages]);

  const headerName =
    data?.[0]?.consumerComplaintDataList?.[0]?.consumerComplaintTypes?.map(
      ({ category }) => category
    );

  const classificationOfComplaint = rowData?.reduce(
    (
      acc,
      { consumerComplaintTypes, classification, classificationOfComplaint }
    ) => {
      const counts = consumerComplaintTypes?.map(({ count }, j) => ({
        [`count${j}`]: count,
      }));
      const mergedCounts = counts?.reduce(
        (a, b) => Object.assign({}, a, b),
        {}
      );

      return [
        ...acc,
        {
          classification: classification,
          classificationOfComplaint: classificationOfComplaint,
          ...mergedCounts,
        },
      ];
    },
    []
  );

  return (
    <>
      <StyledTableContainer maxHeight={maxHeight}>
        <StyledTable>
          <StyledTableHead>
            <TableRow>
              <StyledTableCell rowSpan={3}>
                {t("Classification")}
              </StyledTableCell>
              {headerName?.slice(0, 3)?.map((header, index) => (
                <StyledTableCell key={index} rowSpan={4}>
                  {t(`${header}`)}
                </StyledTableCell>
              ))}
              <StyledTableCell colSpan={5} align="center">
                {t("No.of complaints redressed during the Qtr")}
              </StyledTableCell>
              {headerName?.slice(8)?.map((header, index) => (
                <StyledTableCell key={index} rowSpan={3}>
                  {t(`${header}`)}
                </StyledTableCell>
              ))}
              <StyledTableCell rowSpan={3}>
                {t("Classification of complaint")}
              </StyledTableCell>
            </TableRow>
            <TableRow>
              <StyledTableCell colSpan={2}>
                {t("In stipulated time")}
              </StyledTableCell>
              <StyledTableCell colSpan={2}>
                {t("Beyond stipulated time")}
              </StyledTableCell>
              {headerName?.slice(7, 8)?.map((header, index) => (
                <StyledTableCell key={index} rowSpan={3}>
                  {t(`${header}`)}
                </StyledTableCell>
              ))}
            </TableRow>
            <TableRow>
              {headerName?.slice(3, 5)?.map((header, index) => (
                <StyledTableCell key={index} rowSpan={2}>
                  {t(`${header}`)}
                </StyledTableCell>
              ))}
              {headerName?.slice(5, 7)?.map((header, index) => (
                <StyledTableCell key={index} rowSpan={2}>
                  {t(`${header}`)}
                </StyledTableCell>
              ))}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {classificationOfComplaint?.length ? (
              classificationOfComplaint?.map(
                ({ classificationOfComplaint, classification, ...rest }, i) => (
                  <TableRow key={i}>
                    <TableCell align="center">
                      {allClassifications[classification]}
                    </TableCell>
                    {Object.values(rest)?.map((count, index) => (
                      <TableCell key={index} align="center">
                        {count}
                      </TableCell>
                    ))}
                    <TableCell>{classificationOfComplaint}</TableCell>
                  </TableRow>
                )
              )
            ) : (
              <TableRow>
                <TableCell colSpan={11} className="text-center">
                  {t("No Data Available")}!
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>
      <Pagination paginate={paginate} />
    </>
  );
};

ComplaintTable.propTypes = {
  data: PropTypes.array,
  maxHeight: PropTypes.object,
  t: PropTypes.func,
};

export default withTranslation()(ComplaintTable);
